import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../services/api";
import BottomNav from "../components/BottomNav";
import { getUser } from "../utils/storage";

const BankDetails = () => {
    const navigate = useNavigate();
    const storedUser = getUser();
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [bankInfo, setBankInfo] = useState(null); // From IFSC API

    const [form, setForm] = useState({
        accountNumber: "",
        confirmAccountNumber: "",
        ifsc: "",
        holderName: "",
        bankName: "",
        branchName: ""
    });

    useEffect(() => {
        fetchBankDetails();
    }, []);

    const fetchBankDetails = async () => {
        try {
            const res = await API.get(`/wallet/bank-details?userId=${storedUser.id}`);
            if (res.data.bankDetails) {
                const b = res.data.bankDetails;
                setForm({
                    accountNumber: b.account_number,
                    confirmAccountNumber: b.account_number,
                    ifsc: b.ifsc,
                    holderName: b.holder_name,
                    bankName: b.bank_name || "",
                    branchName: b.branch_name || ""
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const verifyIFSC = async (code) => {
        if (code.length !== 11) return;
        setVerifying(true);
        try {
            const res = await axios.get(`https://ifsc.razorpay.com/${code}`);
            setBankInfo(res.data);
            setForm(prev => ({
                ...prev,
                bankName: res.data.BANK,
                branchName: res.data.BRANCH
            }));
        } catch (err) {
            setBankInfo(null);
            setForm(prev => ({ ...prev, bankName: "Invalid IFSC", branchName: "" }));
        } finally {
            setVerifying(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (form.accountNumber !== form.confirmAccountNumber) return alert("Account Numbers do not match!");
        if (!form.bankName || form.bankName === "Invalid IFSC") return alert("Please provide a valid IFSC");

        setLoading(true);
        try {
            await API.post("/wallet/bank-details", {
                userId: storedUser.id,
                ...form
            });
            alert("Bank Details Saved Successfully!");
            navigate("/wallet");
        } catch (err) {
            alert("Failed to save details");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-theme-bg text-theme-text min-h-screen pb-20 font-sans">
            <div className="p-4 bg-theme-card border-b border-theme-border flex items-center sticky top-0 z-50 shadow-theme-sm">
                <button 
                  onClick={() => navigate(-1)} 
                  className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <h2 className="font-bold text-lg text-theme-text tracking-tight uppercase flex-1 text-center pr-10">Bank Details</h2>
            </div>

            <div className="p-6">
                <div className="bg-theme-card p-6 rounded-[32px] border border-theme-border shadow-theme-sm">
                    <form onSubmit={handleSave} className="space-y-6">
                        <div>
                            <label className="text-[10px] text-theme-text-sec font-bold uppercase tracking-widest block mb-2 opacity-60">Account Holder Name</label>
                            <input
                                type="text"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3.5 text-sm focus:border-accent outline-none transition-all placeholder:opacity-30"
                                value={form.holderName}
                                onChange={(e) => setForm({ ...form, holderName: e.target.value })}
                                placeholder="As per bank records"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-[10px] text-theme-text-sec font-bold uppercase tracking-widest block mb-2 opacity-60">Account Number</label>
                            <input
                                type="password"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3.5 text-sm focus:border-accent outline-none transition-all placeholder:opacity-30"
                                value={form.accountNumber}
                                onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                                placeholder="Enter account number"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-[10px] text-theme-text-sec font-bold uppercase tracking-widest block mb-2 opacity-60">Confirm Account Number</label>
                            <input
                                type="text"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3.5 text-sm focus:border-accent outline-none transition-all placeholder:opacity-30"
                                value={form.confirmAccountNumber}
                                onChange={(e) => setForm({ ...form, confirmAccountNumber: e.target.value })}
                                placeholder="Re-enter account number"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-[10px] text-theme-text-sec font-bold uppercase tracking-widest block mb-2 opacity-60">IFSC Code</label>
                            <input
                                type="text"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3.5 text-sm focus:border-accent outline-none transition uppercase placeholder:opacity-30"
                                value={form.ifsc}
                                onChange={(e) => {
                                    const val = e.target.value.toUpperCase();
                                    setForm({ ...form, ifsc: val });
                                    if (val.length === 11) verifyIFSC(val);
                                }}
                                placeholder="SBIN0001234"
                                maxLength={11}
                                required
                            />
                            {verifying && <p className="text-[9px] text-accent mt-2 font-bold uppercase animate-pulse">Verifying IFSC...</p>}
                            {form.bankName && (
                                <p className={`text-[9px] mt-2 font-bold uppercase tracking-tight ${form.bankName === "Invalid IFSC" ? "text-red-500" : "text-accent"}`}>
                                    {form.bankName} {form.branchName ? `• ${form.branchName}` : ""}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full bg-accent text-white py-4.5 rounded-xl font-bold text-xs uppercase tracking-widest mt-6 transition shadow-theme-lg active:scale-95 ${loading ? "opacity-50" : ""}`}
                        >
                            {loading ? "Saving..." : "Verify & Save Details"}
                        </button>
                    </form>
                </div>

                <div className="mt-8 bg-accent/5 border border-accent/10 p-5 rounded-2xl">
                    <p className="text-[9px] text-accent leading-relaxed font-bold uppercase tracking-wider opacity-60">
                        ⚠️ Please ensure the bank details are absolutely correct. WinroX11 is not responsible for funds transferred to a wrong account.
                    </p>
                </div>
            </div>

            <BottomNav />
        </div>
    );
};

export default BankDetails;
